import { defineStoredJson } from '../utils/storage';
import type { ArrangedEntry } from './columnArrangement';

// ---------------------------------------------------------------------------
/**
 * The columns a user last exported a grid's CSV with: their order and which
 * were ticked. Per USER and grid — on a shared office PC each keeps their
 * own — and on this device only, like the grid's own layout (gridPreferences).
 * Written on export; a column added to the grid since joins in its place (see
 * arrangeColumns).
 */
// ---------------------------------------------------------------------------

type Store = Record<string, ArrangedEntry[]>;

const MAX_ENTRIES = 200;

const csvColumnsStore = defineStoredJson<Store>({
    key: 'csv_export_columns',
    fallback: () => ({}),
    // Corrupt or foreign storage must never take the export down.
    decode: (parsed) =>
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Store)
            : undefined
});

function storeKey(userId: string, gridId: string): string {
    return `${userId}:${gridId}`;
}

export function readCsvColumns(
    userId: string,
    gridId: string
): ArrangedEntry[] {
    const stored = csvColumnsStore.read()[storeKey(userId, gridId)];
    if (!Array.isArray(stored)) {
        return [];
    }
    return stored
        .filter(
            (entry): entry is ArrangedEntry =>
                Boolean(entry) &&
                typeof entry.key === 'string' &&
                typeof entry.shown === 'boolean'
        )
        .slice(0, MAX_ENTRIES);
}

export function writeCsvColumns(
    userId: string,
    gridId: string,
    entries: readonly ArrangedEntry[]
): void {
    const store = csvColumnsStore.read();
    store[storeKey(userId, gridId)] = entries.slice(0, MAX_ENTRIES);
    // Private mode / quota — the export just won't remember.
    csvColumnsStore.write(store);
}
